import { Box, Dialog, Grid, IconButton, Skeleton, Theme, Typography } from '@mui/material'
import { CommonLayout } from '../commonComponents'
import FormikSearchDropdown from '../../lib/formik/FormikSearchDropdown'
import { Form, Formik, FormikProps } from 'formik'
import { MFSubmitButton } from '../../lib/formik'
import { DialogCloseIcon, YellowWarning } from '../../assets/SvgIcons'
import * as Yup from 'yup';
import { ReportsListObj, ReportsListRes } from '../../redux-store/types/api-types'
import { getDeemedOVDReport, getReportsInvestorList } from '../../redux-store/actions/reports'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { RootStateType } from '../../redux-store/reducers'
import { useSnackbar } from 'notistack'
import { maskInfo } from '../../utils/utilityFunctions'
import { reportDownload } from './report-utils'

export default function OVDReports(): JSX.Element {
    const formikRef = useRef<FormikProps<Values>>(null);

    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);
    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const { enqueueSnackbar } = useSnackbar();
    interface Values {
        selectedInvestor: ReportsListObj | null;
    }
    const ValidationSchema = Yup.object().shape({
        selectedInvestor: Yup
            .object()
            .nullable()
            .test(
                "investor-required",
                "Please select an investor",
                function (value) { return !!value }
            )
    })
    const fetchInvestorList = async () => {
        try {
            setLoading(true);
            const payload = {
                rmId: `${iwappRmId}`
            }
            const response = await (dispatch(getReportsInvestorList(payload)) as unknown as Promise<ReportsListRes>);
            setInvestorListData(response);
            if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (e) {
            console.log((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvestorList();
    }, []);

    const initialValues: Values = {
        selectedInvestor: null,
    };

    const handleSubmit = async ({
        selectedInvestor
    }: Values) => {
        try {
            setIsDownloading(true)
            const payload = {
                investorName: selectedInvestor?.customerName ?? '',
                clientCode: selectedInvestor?.clientCode ?? '',
            }

            const response = await (dispatch(getDeemedOVDReport(payload)) as unknown as Promise<any>)

            reportDownload(response as unknown as Blob, 'deemedOvdReport.xlsx');
            enqueueSnackbar('Report downloaded successfully.', {
                variant: 'success',
                autoHideDuration: 2000,
            });

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsDownloading(false)
        }
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestor: null,
            })

            return
        }

        setValues({
            ...values,
            selectedInvestor: selectedObj as ReportsListObj,
        })
    }

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    return (
        <CommonLayout>
            <>
                <Typography
                    variant='h1'
                    sx={{
                        alignSelf: { xs: 'center' },
                        mb: 3
                    }}>
                    Deemed OVD Reports
                </Typography>
                <Formik
                    innerRef={formikRef}
                    validationSchema={ValidationSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {() => {
                        return (
                            <Form>
                                <Grid container item xs={12} gap={2} sx={{ mt: 3, border: '1px solid #E0E0E0', borderRadius: '10px', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                                    <Grid item xs={12} md={5}>
                                        {(loading || isNavbarInfoLoading) &&
                                            <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />
                                        }
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <FormikSearchDropdown
                                                name='selectedInvestor'
                                                label='Select Investor *'
                                                options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                    ...obj,
                                                    label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.accountType ? " - " + obj.accountType : ""}`,
                                                    value: obj.clientCode
                                                }))}
                                                onChange={handleInvestorChange}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && <MFSubmitButton
                                            label="Download"
                                            loadingEnable={isDownloading}
                                        />}
                                    </Grid>
                                </Grid>
                            </Form>
                        )
                    }}
                </Formik>
                <Dialog
                    onClose={handleNoInvestorDialogClose}
                    open={isNoInvestorDialogVisible}
                    maxWidth='sm'
                    sx={(theme: Theme) => ({
                        '& .MuiPaper-root': {
                            borderRadius: '12px',
                            backgroundColor: theme.palette.common.white,
                            padding: "22px 24px",
                            width: "533px",
                        },
                    })}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            rowGap: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    columnGap: "12px",
                                    alignItems: "center"
                                }}
                            >
                                <YellowWarning />
                                <Typography
                                    variant="h2"
                                    sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                                >
                                    No Investor Found!!
                                </Typography>
                            </Box>
                            <IconButton
                                aria-label="close"
                                onClick={handleNoInvestorDialogClose}
                                sx={{
                                    padding: "5px"
                                }}
                            >
                                <DialogCloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Dialog>
            </>
        </CommonLayout >

    )
}
